import { AuthClient, UserAuthToken } from "../../../domain/auth/user-auth/auth-client";
import { useCallback, useEffect, useMemo, useReducer } from "react";
import { persistUserAccessToken, removeUserAccessToken } from "./read-user-auth-token";
import { reducer } from "./reducer";
import { usePeriodicallyDeps } from "../../../hooks/use-periodically";
import { intercomLogout, TOKEN_REFRESH_CHECK_INTERVAL } from "./use-user-aspect";
import { retry } from "@swo/shared";
import { useOpaqueValue } from "../../../hooks/use-opaque-value";
import { debug } from "../../../utils/development/development";
import { createNewAnonymousUser, getStateFromPersistence } from "./getStateFromPersistence";
import { useTabBackInFocus } from "../../../hooks/use-tab-visibility";
import { useQueueWithProcessor } from "../../../hooks/use-queue";
import { UserAspectPublicAction } from "./public-action";
import { useAsyncDataFetches } from "./use-async-data-fetches";

export const USER_TOKEN_BROWSER_STORAGE_KEY = "user-auth-token";
export const ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY = "anonymous-token";

export const logger = debug("UserAspectState");

export function useUserAspectState() {
  const [state, dispatch] = useReducer(reducer, null);
  const stateRef = useOpaqueValue(state);

  const { append } = useQueueWithProcessor<"tick">(async () => {
    const current = stateRef.current;

    if (current) {
      await refreshTokenIfPossible(current.tag, current.authToken);
      return;
    }

    logger("Reinitializing state");

    const persistedState = getStateFromPersistence();

    if (persistedState) {
      const maybeValidToken = await refreshTokenIfPossible(persistedState.tag, persistedState.authToken);
      if (maybeValidToken) {
        dispatch({ type: "re-initialize", value: { ...persistedState, authToken: maybeValidToken } });
      }
    } else {
      logger(`Creating new anonymous user identity.`);

      try {
        // Probably a first time visit,
        const user = await createNewAnonymousUser();
        dispatch({ type: "anonymous-token-fetched", value: user.authToken });

        persistUserAccessToken(ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY, user.authToken);
      } catch (e) {
        dispatch({
          type: "anonymous-token-fetched",
          value: {
            token_type: "Bearer",
            access_token: "",
            expires_at: new Date().getTime() + 1000 * 60 * 60 * 24 * 365,
            expires_in: 1000 * 60 * 60 * 24 * 365,
            refresh_token: "",
            scope: "",
          },
        });
      }
    }
  });

  function refreshTokenIfPossible(tag: "logged-in-user" | "anonymous-user", currentToken: UserAuthToken) {
    logger("Token freshness check");
    const expiresAt = currentToken.expires_at;
    const currentTime = Date.now();
    const freshnessThreshold = 30 * 60;

    const dT = Math.round((expiresAt - currentTime) / 1000);

    if (dT > freshnessThreshold) {
      logger(`Token still valid for ${dT} seconds`);

      // do nothing, still valid for next >= ${freshnessThreshold} seconds
      return Promise.resolve(currentToken);
    } else {
      logger(`Will attempt token refresh (${dT} seconds)`);

      return forceRefreshToken(tag, currentToken);
    }
  }

  function tokenRefreshAction(tag: "logged-in-user" | "anonymous-user", currentToken: UserAuthToken) {
    const userAuthTokenRefreshWithRetries = retry(() => AuthClient.refreshAuthToken(currentToken), {
      delayMs: 1000,
      count: 3,
    });

    logger("Token refresh attempted");

    return userAuthTokenRefreshWithRetries();
  }

  function forceRefreshToken(
    tag: "logged-in-user" | "anonymous-user",
    currentToken: UserAuthToken,
  ): Promise<UserAuthToken | null> {
    return tokenRefreshAction(tag, currentToken)
      .then(token => {
        logger(`Token refreshed.`);
        dispatch({ type: "token-refreshed", value: token });

        switch (tag) {
          case "logged-in-user": {
            persistUserAccessToken(USER_TOKEN_BROWSER_STORAGE_KEY, token);
            break;
          }
          case "anonymous-user": {
            persistUserAccessToken(ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY, token);
            break;
          }
          default: {
            const _: never = tag;
          }
        }

        return token;
      })
      .catch(e => {
        logger(`Token refresh failed.`);
        console.log(e);

        if (tag === "logged-in-user") {
          handleLogoutEvent();
        } else {
          removeUserAccessToken(ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY);
        }

        dispatch({ type: "token-refresh-failed" });

        return null;
      });
  }

  useEffect(() => {
    append("tick");
  }, []);

  usePeriodicallyDeps(
    TOKEN_REFRESH_CHECK_INTERVAL,
    () => {
      append("tick");
    },
    [],
  );

  useTabBackInFocus(() => {
    append("tick");
  }, []);

  useAsyncDataFetches(state, dispatch);

  const memoState = useMemo(() => state, [JSON.stringify(state)]);

  const publicDispatch = useCallback(
    (action: UserAspectPublicAction) => {
      switch (action.type) {
        case "token-appears-to-be-invalid": {
          // panic mode
          handleLogoutEvent();
          removeUserAccessToken(ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY);

          return dispatch({ type: "token-appears-to-be-invalid" });
        }

        case "user-logged-in": {
          removeUserAccessToken(ANONYMOUS_TOKEN_BROWSER_STORAGE_KEY);
          persistUserAccessToken(USER_TOKEN_BROWSER_STORAGE_KEY, action.value);

          return dispatch(action);
        }

        case "user-logged-out": {
          handleLogoutEvent();

          return dispatch(action);
        }
        default: {
          const _: never = action;
          return _;
        }
      }
    },
    [dispatch],
  );

  return [memoState, publicDispatch] as const;
}

function handleLogoutEvent() {
  removeUserAccessToken(USER_TOKEN_BROWSER_STORAGE_KEY);
  intercomLogout();
}
